import ShareButton from './ShareButton.jsx';
import { UI_TEXT } from './utils/constants.js';
import { ACTIONS } from './utils/tournamentReducer.js';
import './ResultScreen.css';
import { useTournamentState } from './TournamentContext.jsx';
import { useEffect } from 'react';

/**
 * ResultScreen - Display tournament winner
 * @param {Object} props
 * @param {Object} props.champion - Winner candidate object
 * @param {Function} props.onRestart - Callback to restart tournament
 * @param {Function} props.onShowRankings - Callback to show rankings
 * @param {Function} props.dispatch - Dispatch function from reducer
 * @param {string} props.language - Language code ('ko' or 'en')
 */
export default function ResultScreen({
  champion,
  onRestart,
  onShowRankings,
  dispatch,
  token,
  language = 'ko',
}) {
  const state = useTournamentState();
  const text = UI_TEXT[language] || UI_TEXT.ko;
  const championName = language === 'en' ? champion.nameEn || champion.name : champion.name;

  // Send result to server
  useEffect(() => {
    const submitResultAndFetchRankings = async () => {
      try {
        // 1. 보내 - PATCH로 winner 전송
        const patchResponse = await fetch('https://wooltaricup-qmlofqf3ja-uc.a.run.app', {
          method: 'PATCH',
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
            'x-campaign-id': 'wolrd-cup-2026',
            // 'x-test-token': 'dnfxkflxptmxm'
          },
          body: JSON.stringify({ winner: champion.id }),
        });

        if (!patchResponse.ok) {
          throw new Error(`PATCH failed: ${patchResponse.status}`);
        }

        // 2. 서버에서 방금 보낸 결과를 포함시켜 계산한거 다시 받아 (wins 순서대로 sort 되어서 옴)
        const getResponse = await fetch('https://wooltaricup-qmlofqf3ja-uc.a.run.app', {
          method: 'GET',
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
            'x-campaign-id': 'wolrd-cup-2026',
            'x-game-state': 'result',
            // 'x-test-token': 'dnfxkflxptmxm',
          },
        });

        if (!getResponse.ok) {
          throw new Error(`GET failed: ${getResponse.status}`);
        }

        const data = await getResponse.json();

        // 3. Transform entries for ranking screen
        const entries = [];
        if (data.campaign?.entries && Array.isArray(data.campaign.entries)) {
          data.campaign.entries.forEach((entry) => {
            entries.push({
              id: entry.id,
              name: entry.title,
              nameEn: entry.titleEn || entry.title,
              img: entry.imgUrl,
              wins: entry.wins || 0,
            });
          });
        }

        const campaignData = {
          id: data.campaign?.id || 'wolrd-cup-2026',
          start: data.campaign?.start,
          end: data.campaign?.end,
          entries: entries,
        };

        // 4. reducer 에 저장시켜
        dispatch({
          type: ACTIONS.SET_CAMPAIGN_DATA,
          payload: campaignData,
        });
      } catch (error) {
        console.error('Failed to submit result or fetch rankings:', error);
      }
    };

    submitResultAndFetchRankings();
  }, [champion, dispatch]);

  return (
    <div className="result-screen">
      <div className="result-screen__content">
        <h1 className="result-screen__title">{text.champion}</h1>

        <div className="result-screen__champion">
          <div className="result-screen__trophy">🏆</div>
          <div className="result-screen__champion-image-wrapper">
            <img src={champion.img} alt={championName} className="result-screen__champion-image" />
          </div>
          <h2 className="result-screen__champion-name">{championName}</h2>
        </div>

        <div className="result-screen__actions">
          <button
            className="result-screen__restart-button"
            style={{
              background: state.themeColor,
            }}
            onClick={onRestart}
            type="button"
          >
            {text.restart}
          </button>

          <button
            className="result-screen__show-ranking-button"
            style={{
              background: state.themeColor,
            }}
            type="button"
            onClick={onShowRankings}
            data-amp-event={state.eventName}
            data-amp-section="wooltari_cup_show_ranking"
            data-amp-title="wooltari_cup_show_ranking"
            data-track-event={state.eventName}
            data-track-category="wooltari_cup_show_ranking"
            data-track-label="wooltari_cup_show_ranking"
          >
            {text.showRankings}
          </button>
          <ShareButton champion={champion} language={language} />
        </div>
      </div>
    </div>
  );
}